import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Drawer,
  EmptyState,
  Field,
  IconBtn,
  Input,
  PageHeader,
  Select,
  Spinner,
  Table,
  useConfirm,
  useToast,
} from "../../../design-system";
import { Icon } from "../../../design-system/Icon";
import useCurrentMembership from "../../../helpers/useCurrentMembership";
import { startImpersonation } from "../../../helpers/impersonation";
import {
  changeMembershipRole,
  clearMembershipCapability,
  createEmployee,
  inviteToWorkspace,
  listMembershipCapabilities,
  listWorkspaceAccess,
  resendInvitation,
  revokeInvitation,
  revokeWorkspaceAccess,
  setMembershipCapability,
} from "../../../helpers/kronaly_helper";

const ROLE_LABEL = {
  owner: "Propietario",
  admin: "Administrador",
  manager: "Manager",
  employee: "Empleado",
};

const ROLE_TONE = {
  owner: "warning",
  admin: "primary",
  manager: "info",
  employee: "neutral",
};

function unwrap(r) {
  if (r && typeof r === "object" && "data" in r) return r.data;
  return r;
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch (_) {
    return "—";
  }
}

export default function AccesosPage() {
  document.title = "Accesos | Kronaly";
  const { me, isAdmin, role: actorRole, loading: membershipLoading } = useCurrentMembership();
  const canImpersonate = !!me?.can_impersonate;
  const toast = useToast();
  const confirm = useConfirm();
  const [memberships, setMemberships] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  // Fila para la que se está creando un perfil de empleado (atajo desde Accesos).
  const [createProfileFor, setCreateProfileFor] = useState(null);
  const [capabilitiesFor, setCapabilitiesFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await listWorkspaceAccess();
      const data = unwrap(r) || {};
      setMemberships(Array.isArray(data.memberships) ? data.memberships : []);
      setPendingInvitations(Array.isArray(data.pending_invitations) ? data.pending_invitations : []);
    } catch (e) {
      setError(typeof e === "string" ? e : e?.message || "No se pudieron cargar los accesos.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!membershipLoading && isAdmin) load();
  }, [membershipLoading, isAdmin, load]);

  const actorIsOwner = actorRole === "owner";

  // Roles asignables por el actor: si es admin no puede asignar/quitar owner.
  const availableRoles = useMemo(() => {
    if (actorIsOwner) return ["owner", "admin", "manager", "employee"];
    return ["admin", "manager", "employee"];
  }, [actorIsOwner]);

  const handleRoleChange = async (row, newRole) => {
    if (newRole === row.role) return;
    setSavingId(row.membership_id);
    try {
      await changeMembershipRole(row.membership_id, newRole);
      toast.success(
        "Rol actualizado",
        `${row.email} ahora es ${ROLE_LABEL[newRole]?.toLowerCase() || newRole}.`,
      );
      await load();
    } catch (e) {
      toast.error(
        "No se pudo cambiar el rol",
        typeof e === "string" ? e : e?.message || "Error desconocido",
      );
    } finally {
      setSavingId(null);
    }
  };

  const handleRevoke = async (row) => {
    const ok = await confirm({
      title: `Revocar acceso de ${row.email}`,
      description:
        "El usuario perderá inmediatamente acceso al workspace. Si vuelve a necesitarlo tendrás que invitarlo de nuevo.",
      confirmLabel: "Revocar acceso",
      tone: "danger",
    });
    if (!ok) return;
    setSavingId(row.membership_id);
    try {
      await revokeWorkspaceAccess(row.membership_id);
      toast.success("Acceso revocado", `${row.email} ya no tiene acceso.`);
      await load();
    } catch (e) {
      toast.error(
        "No se pudo revocar",
        typeof e === "string" ? e : e?.message || "Error desconocido",
      );
    } finally {
      setSavingId(null);
    }
  };

  const handleImpersonate = async (row) => {
    const ok = await confirm({
      title: `Entrar como ${row.name}`,
      description:
        `Abrirás una sesión viendo Kronaly como ${row.email} para verificar cosas. ` +
        "Tu sesión seguirá activa: podrás volver a tu cuenta desde la barra superior. " +
        "Quedará registrado en el log de auditoría.",
      confirmLabel: "Entrar como este usuario",
      tone: "primary",
    });
    if (!ok) return;
    setSavingId(row.membership_id);
    try {
      await startImpersonation(row.email);
      // startImpersonation recarga la app; no hace falta más aquí.
    } catch (e) {
      setSavingId(null);
      toast.error(
        "No se pudo impersonar",
        typeof e === "string" ? e : e?.message || "Error desconocido",
      );
    }
  };

  const handleResend = async (inv) => {
    setSavingId(inv.invitation_id);
    try {
      await resendInvitation(inv.invitation_id);
      toast.success("Invitación reenviada", `Email enviado de nuevo a ${inv.email}.`);
      await load();
    } catch (e) {
      toast.error(
        "No se pudo reenviar",
        typeof e === "string" ? e : e?.message || "Error desconocido",
      );
    } finally {
      setSavingId(null);
    }
  };

  const handleRevokeInvitation = async (inv) => {
    const ok = await confirm({
      title: `Revocar invitación a ${inv.email}`,
      description:
        "El enlace dejará de funcionar inmediatamente. Si vuelve a necesitarlo, puedes invitar a esa persona de nuevo.",
      confirmLabel: "Revocar invitación",
      tone: "danger",
    });
    if (!ok) return;
    setSavingId(inv.invitation_id);
    try {
      await revokeInvitation(inv.invitation_id);
      toast.success("Invitación revocada", `${inv.email} ya no podrá unirse con ese enlace.`);
      await load();
    } catch (e) {
      toast.error(
        "No se pudo revocar la invitación",
        typeof e === "string" ? e : e?.message || "Error desconocido",
      );
    } finally {
      setSavingId(null);
    }
  };

  if (membershipLoading) {
    return (
      <div className="kr-page" style={{ display: "flex", justifyContent: "center", padding: 48 }}>
        <Spinner />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="kr-page">
        <EmptyState
          icon="ShieldOff"
          title="Acceso restringido"
          description="Solo el propietario o un administrador del workspace pueden gestionar accesos."
        />
      </div>
    );
  }

  return (
    <div className="kr-page">
      <PageHeader
        eyebrow="Workspace"
        title="Accesos"
        description="Gestiona quién tiene acceso al workspace y con qué rol. Para crear empleados que fichen, usa Empleados."
        actions={
          <Button variant="primary" icon="UserPlus" onClick={() => setInviteOpen(true)}>
            Invitar usuario
          </Button>
        }
      />

      {error && <Alert tone="danger">{error}</Alert>}

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
          <Spinner />
        </div>
      ) : memberships.length === 0 && pendingInvitations.length === 0 ? (
        <EmptyState
          icon="Users"
          title="Aún no hay accesos"
          description="Invita al primer miembro para empezar a colaborar."
          action={{
            label: "Invitar usuario",
            icon: "UserPlus",
            onClick: () => setInviteOpen(true),
          }}
        />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          <SectionTitle
            title="Miembros activos"
            count={memberships.length}
          />
          <Table>
          <thead>
            <tr>
              <th style={th}>Miembro</th>
              <th style={th}>Estado</th>
              <th style={th}>Tipo</th>
              <th style={th}>Rol</th>
              <th style={th}>Desde</th>
              <th style={{ ...th, textAlign: "right" }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {memberships.map((r) => {
              const canEditRow = actorIsOwner || r.role !== "owner";
              const roleOptions = canEditRow
                ? // Si la fila ya tiene un rol no asignable (caso: admin
                  // viendo a un owner pre-existente), lo añadimos para
                  // no perderlo del select. Pero canEditRow=false en ese
                  // caso, así que aquí siempre es seguro.
                  availableRoles
                : [r.role];
              return (
                <tr key={r.membership_id}>
                  <td style={td}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <span style={{ fontWeight: 600, color: "var(--kr-fg-strong)" }}>
                        {r.name}
                      </span>
                      <span
                        style={{
                          fontFamily: "var(--kr-font-mono)",
                          fontSize: 12,
                          color: "var(--kr-fg-muted)",
                        }}
                      >
                        {r.email}
                      </span>
                    </div>
                  </td>
                  <td style={td}>
                    {r.user_status === "invited" ? (
                      <Badge tone="warning" dot>
                        Pendiente
                      </Badge>
                    ) : (
                      <Badge tone="success" dot>
                        Activo
                      </Badge>
                    )}
                  </td>
                  <td style={td}>
                    {r.is_employee ? (
                      <Badge tone="info">Empleado</Badge>
                    ) : (
                      <Badge tone="neutral">Solo acceso</Badge>
                    )}
                  </td>
                  <td style={td}>
                    <Select
                      value={r.role}
                      onChange={(e) => handleRoleChange(r, e.target.value)}
                      disabled={!canEditRow || savingId === r.membership_id}
                      style={{ minWidth: 150 }}
                    >
                      {roleOptions.map((roleKey) => (
                        <option key={roleKey} value={roleKey}>
                          {ROLE_LABEL[roleKey] || roleKey}
                        </option>
                      ))}
                    </Select>
                  </td>
                  <td style={{ ...td, color: "var(--kr-fg-muted)" }}>
                    {formatDate(r.member_since)}
                  </td>
                  <td style={{ ...td, textAlign: "right" }}>
                    <div style={{ display: "inline-flex", gap: 4 }}>
                      {canImpersonate &&
                        r.user_status !== "invited" &&
                        r.email !== me?.email && (
                          <IconBtn
                            icon="LogIn"
                            title="Entrar como este usuario"
                            onClick={() => handleImpersonate(r)}
                            disabled={savingId === r.membership_id}
                          />
                        )}
                      <IconBtn
                        icon="SlidersHorizontal"
                        title="Capacidades"
                        onClick={() => setCapabilitiesFor(r)}
                        disabled={savingId === r.membership_id}
                      />
                      {!r.is_employee && (
                        <IconBtn
                          icon="UserPlus"
                          title="Crear perfil de empleado"
                          onClick={() => setCreateProfileFor(r)}
                          disabled={savingId === r.membership_id}
                        />
                      )}
                      <IconBtn
                        icon="Trash2"
                        title="Revocar acceso"
                        onClick={() => handleRevoke(r)}
                        disabled={!canEditRow || savingId === r.membership_id}
                        style={{ color: "var(--kr-danger-600)" }}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>

        {pendingInvitations.length > 0 && (
          <>
            <SectionTitle
              title="Invitaciones pendientes"
              count={pendingInvitations.length}
              description="Estos emails recibieron un enlace pero todavía no han aceptado."
            />
            <Table>
              <thead>
                <tr>
                  <th style={th}>Email</th>
                  <th style={th}>Rol asignado</th>
                  <th style={th}>Caduca</th>
                  <th style={{ ...th, textAlign: "right" }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {pendingInvitations.map((inv) => (
                  <tr key={inv.invitation_id}>
                    <td style={td}>
                      <span
                        style={{
                          fontFamily: "var(--kr-font-mono)",
                          fontSize: 13,
                          color: "var(--kr-fg-strong)",
                        }}
                      >
                        {inv.email}
                      </span>
                    </td>
                    <td style={td}>
                      <Badge tone={ROLE_TONE[inv.role] || "neutral"}>
                        {ROLE_LABEL[inv.role] || inv.role}
                      </Badge>
                    </td>
                    <td
                      style={{
                        ...td,
                        color: inv.is_expired ? "var(--kr-danger-700)" : "var(--kr-fg-muted)",
                      }}
                    >
                      {inv.is_expired ? "Caducada" : formatDate(inv.expires_at)}
                    </td>
                    <td style={{ ...td, textAlign: "right" }}>
                      <div style={{ display: "inline-flex", gap: 4 }}>
                        <IconBtn
                          icon="Send"
                          title={inv.is_expired ? "Reenviar (renueva enlace)" : "Reenviar"}
                          onClick={() => handleResend(inv)}
                          disabled={savingId === inv.invitation_id}
                        />
                        <IconBtn
                          icon="X"
                          title="Revocar invitación"
                          onClick={() => handleRevokeInvitation(inv)}
                          disabled={savingId === inv.invitation_id}
                          style={{ color: "var(--kr-danger-600)" }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
        </div>
      )}

      <InviteDrawer
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        availableRoles={availableRoles}
        onInvited={async () => {
          setInviteOpen(false);
          await load();
        }}
        toast={toast}
      />

      <CreateProfileDrawer
        row={createProfileFor}
        onClose={() => setCreateProfileFor(null)}
        onCreated={async () => {
          setCreateProfileFor(null);
          await load();
        }}
        toast={toast}
      />

      <CapabilitiesDrawer
        row={capabilitiesFor}
        onClose={() => setCapabilitiesFor(null)}
        toast={toast}
      />
    </div>
  );
}

function CapabilitiesDrawer({ row, onClose, toast }) {
  const open = row !== null;
  const [caps, setCaps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [savingCap, setSavingCap] = useState(null);

  const load = useCallback(async () => {
    if (!row) return;
    setLoading(true);
    setError(null);
    try {
      const r = await listMembershipCapabilities(row.membership_id);
      setCaps(unwrap(r) || []);
    } catch (e) {
      setError(typeof e === "string" ? e : e?.message || "No se pudieron cargar las capacidades.");
    } finally {
      setLoading(false);
    }
  }, [row]);

  useEffect(() => {
    if (open) load();
    else {
      setCaps([]);
      setError(null);
    }
  }, [open, load]);

  const handleSet = async (cap, granted) => {
    setSavingCap(cap.capability);
    try {
      await setMembershipCapability(row.membership_id, cap.capability, granted);
      await load();
    } catch (e) {
      toast.error(
        "No se pudo guardar",
        typeof e === "string" ? e : e?.message || "Error desconocido",
      );
    } finally {
      setSavingCap(null);
    }
  };

  const handleClear = async (cap) => {
    setSavingCap(cap.capability);
    try {
      await clearMembershipCapability(row.membership_id, cap.capability);
      await load();
    } catch (e) {
      toast.error(
        "No se pudo restablecer",
        typeof e === "string" ? e : e?.message || "Error desconocido",
      );
    } finally {
      setSavingCap(null);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={row ? `Capacidades · ${row.name}` : "Capacidades"}
      size="lg"
      footer={
        <Button variant="ghost" onClick={onClose}>
          Cerrar
        </Button>
      }
    >
      {!row ? null : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <Alert tone="info">
            Estas capacidades sobreescriben los <strong>defaults del rol</strong> (
            {ROLE_LABEL[row.role] || row.role}) sólo para <strong>{row.email}</strong>. Si un
            módulo está desactivado en el workspace, sus capacidades quedan en gris y no aplican.
          </Alert>
          {error && <Alert tone="danger">{error}</Alert>}
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
              <Spinner />
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {caps.map((cap) => (
                <CapabilityRow
                  key={cap.capability}
                  cap={cap}
                  saving={savingCap === cap.capability}
                  onSet={handleSet}
                  onClear={handleClear}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
}

function CapabilityRow({ cap, saving, onSet, onClear }) {
  const hasOverride = cap.override !== null;
  const effectiveLabel = cap.effective ? "Permitido" : "Denegado";
  const sourceLabel = !cap.module_enabled
    ? "Módulo desactivado"
    : hasOverride
      ? cap.override
        ? "Concedido explícitamente"
        : "Denegado explícitamente"
      : `Por defecto del rol (${cap.role_default ? "permitido" : "denegado"})`;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        padding: 12,
        border: "1px solid var(--kr-border)",
        borderRadius: 8,
        background: !cap.module_enabled ? "var(--kr-bg-subtle)" : "var(--kr-bg-surface)",
        opacity: !cap.module_enabled ? 0.7 : 1,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontWeight: 600, fontSize: 14, color: "var(--kr-fg-strong)" }}>
            {cap.label}
          </span>
          <Badge tone={cap.effective && cap.module_enabled ? "success" : "neutral"} dot>
            {effectiveLabel}
          </Badge>
          {hasOverride && cap.module_enabled && (
            <Badge tone={cap.override ? "primary" : "danger"}>Override</Badge>
          )}
        </div>
        <div style={{ fontSize: 12, color: "var(--kr-fg-muted)", marginTop: 4 }}>
          {cap.description}
        </div>
        <div
          style={{
            fontFamily: "var(--kr-font-mono)",
            fontSize: 11,
            color: "var(--kr-fg-subtle)",
            marginTop: 4,
          }}
        >
          {cap.capability} · {sourceLabel}
        </div>
      </div>
      <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
        <Button
          variant={cap.override === true ? "primary" : "secondary"}
          size="sm"
          icon="Check"
          onClick={() => onSet(cap, true)}
          disabled={saving || !cap.module_enabled}
        >
          Conceder
        </Button>
        <Button
          variant={cap.override === false ? "dangerSoft" : "secondary"}
          size="sm"
          icon="X"
          onClick={() => onSet(cap, false)}
          disabled={saving || !cap.module_enabled}
        >
          Denegar
        </Button>
        {hasOverride && (
          <Button
            variant="ghost"
            size="sm"
            icon="RotateCcw"
            onClick={() => onClear(cap)}
            disabled={saving}
            title="Quitar override (volver al default del rol)"
          >
            Quitar
          </Button>
        )}
      </div>
    </div>
  );
}

function CreateProfileDrawer({ row, onClose, onCreated, toast }) {
  const open = row !== null;
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [position, setPosition] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setFirstName("");
      setLastName("");
      setPhone("");
      setPosition("");
      setSubmitting(false);
    }
  }, [open]);

  const submit = async () => {
    if (!firstName.trim() || !lastName.trim()) return;
    setSubmitting(true);
    try {
      await createEmployee({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: row.email,
        phone: phone.trim() || null,
        position: position.trim() || null,
      });
      toast.success(
        "Perfil de empleado creado",
        `${row.email} ya puede fichar y aparecer en Empleados. Su rol (${row.role}) se conserva.`,
      );
      await onCreated();
    } catch (e) {
      toast.error(
        "No se pudo crear el perfil",
        typeof e === "string" ? e : e?.message || "Error desconocido",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Crear perfil de empleado"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={!firstName.trim() || !lastName.trim() || submitting}
            icon="UserPlus"
          >
            {submitting ? "Creando…" : "Crear perfil"}
          </Button>
        </>
      }
    >
      {row && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <Alert tone="info">
            Vas a crear un perfil de empleado para <strong>{row.email}</strong>. Su rol actual{" "}
            (<strong>{ROLE_LABEL[row.role] || row.role}</strong>) se conserva — sólo le añadimos los
            datos HR para que pueda fichar y aparecer en Empleados.
          </Alert>
          <Field label="Nombre" required>
            <Input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Nombre"
              autoFocus
            />
          </Field>
          <Field label="Apellidos" required>
            <Input
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Apellidos"
            />
          </Field>
          <Field label="Teléfono">
            <Input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+34 …"
            />
          </Field>
          <Field label="Cargo">
            <Input
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              placeholder="Tesorería, Encargado IT, …"
            />
          </Field>
        </div>
      )}
    </Drawer>
  );
}

function SectionTitle({ title, count, description }) {
  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 6 }}>
        <h3
          style={{
            margin: 0,
            fontSize: 14,
            fontWeight: 600,
            color: "var(--kr-fg-strong)",
            letterSpacing: "-0.005em",
          }}
        >
          {title}
        </h3>
        {typeof count === "number" && (
          <span style={{ fontSize: 12, color: "var(--kr-fg-muted)" }}>· {count}</span>
        )}
      </div>
      {description && (
        <p style={{ margin: 0, fontSize: 12, color: "var(--kr-fg-muted)" }}>{description}</p>
      )}
    </div>
  );
}

function InviteDrawer({ open, onClose, availableRoles, onInvited, toast }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState(availableRoles[availableRoles.length - 1] || "employee");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setEmail("");
      setRole(availableRoles[availableRoles.length - 1] || "employee");
      setSubmitting(false);
    }
  }, [open, availableRoles]);

  const submit = async () => {
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await inviteToWorkspace(email.trim(), role);
      toast.success(
        "Invitación enviada por email",
        `${email.trim()} recibirá el enlace para unirse al workspace.`,
      );
      await onInvited();
    } catch (e) {
      toast.error(
        "No se pudo invitar",
        typeof e === "string" ? e : e?.message || "Error desconocido",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Invitar usuario al workspace"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={!email.trim() || submitting}
            icon="Send"
          >
            {submitting ? "Enviando…" : "Invitar"}
          </Button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <Field label="Email del usuario" required>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="usuario@empresa.com"
            autoFocus
          />
        </Field>
        <Field
          label="Rol en el workspace"
          required
          help="El rol determina qué puede hacer dentro del workspace. Para que pueda fichar como empleado, créale un perfil después desde Empleados."
        >
          <Select value={role} onChange={(e) => setRole(e.target.value)}>
            {availableRoles.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABEL[r] || r}
              </option>
            ))}
          </Select>
        </Field>
        <Alert tone="info">
          <strong>Se enviará un email con el enlace de invitación.</strong> Si el usuario ya tiene
          cuenta en Kronaly, sólo tendrá que aceptar; si no, podrá crearla en el mismo flujo. El
          enlace caduca a los 7 días.
        </Alert>
      </div>
    </Drawer>
  );
}

const th = {
  textAlign: "left",
  padding: "10px 16px",
  borderBottom: "1px solid var(--kr-border)",
  background: "var(--kr-bg-subtle)",
  fontSize: 11,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "var(--kr-fg-muted)",
  fontWeight: 600,
};

const td = {
  padding: "12px 16px",
  borderBottom: "1px solid var(--kr-border-subtle)",
  verticalAlign: "middle",
};
